
import { Component, Vue } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import { AuthMixin } from '@/mixins/AuthMixin';
import { WhatsAppApi } from '@/api/WhatsAppApi';
import { WhatsAppMessageTemplate, WhatsAppTemplateMessageComponent, SupportedPlatform } from 'ignite360-core';
import { BotMixin } from '@/mixins/BotMixin';
import { ProjectViewMixin } from '@/mixins/ProjectViewMixin';
import { Watch } from 'vue-property-decorator';
import { SelectOption } from '@/types';
import { PLATFORMS_METADATA } from '@/constants';

interface SendMessageData {
  templateName: string;
  number: string;
  parameters: Record<string, string>;
}

@Component({
  name: 'send-message-card',
  components: {},
})
export default class SendMessageCard extends mixins(AuthMixin, ProjectViewMixin, BotMixin) {
  messageData: SendMessageData = {
    templateName: '',
    number: '',
    parameters: {},
  };

  isValidPhoneNumber = true;
  showError = false;

  isSubmitting = false;

  messageTemplates: WhatsAppMessageTemplate[] = [];

  get whatsAppLogo(): string {
    return (
      PLATFORMS_METADATA.find((platform) => platform.value === SupportedPlatform.WhatsApp)?.image ||
      ''
    );
  }

  get approvedTemplates(): SelectOption[] {
    const templates = this.messageTemplates
      .filter((template) => template.status === 'APPROVED')
      .map((template) => ({
        label: template.name,
        value: template.name,
      }));
    return templates;
  }

  get selectedTemplate(): WhatsAppMessageTemplate | null {
    if (!this.messageData.templateName) return null;
    console.log(
      'selectedTemplate:',
      this.messageTemplates.find((t) => t.name === this.messageData.templateName),
    );
    return this.messageTemplates.find((t) => t.name === this.messageData.templateName) || null;
  }

  get componentsWithParameters(): WhatsAppTemplateMessageComponent[] {
    if (!this.selectedTemplate?.components) return [];
    return this.selectedTemplate.components.filter(
      (component) => component.text && component.text.includes('{{'),
    );
  }

  getComponentParameters(
    component: WhatsAppTemplateMessageComponent,
  ): Array<{ label: string; example: string }> {
    if (!component.text) return [];

    // Extract parameters from text (e.g., "{{1}}", "{{2}}")
    const paramMatches = component.text.match(/{{(\d+)}}/g) || [];
    const params: Array<{ label: string; example: string }> = [];

    paramMatches.forEach((match, index) => {
      const paramNumber = match.match(/\d+/)?.[0];
      if (!paramNumber) return;

      // Get example from component.example.body_text if available
      const example = component.example?.body_text?.[0]?.[index] || `Parameter ${paramNumber}`;

      params.push({
        label: `Parameter ${paramNumber}`,
        example: example,
      });
    });

    return params;
  }

  get hasParameters(): boolean {
    return this.componentsWithParameters.some(
      (component) => this.getComponentParameters(component).length > 0,
    );
  }

  async created() {
    await this.loadMessageTemplates();
  }

  async loadMessageTemplates() {
    try {
      this.messageTemplates = await WhatsAppApi.getTemplates(this.$route.params.id);
    } catch (error) {
      console.error('Error loading templates:', error);
      this.$notify.error({
        title: 'Error',
        description: 'Failed to load WhatsApp Message Templates',
      });
    }
  }

  get canSubmit(): boolean {
    const hasRequiredFields =
      !!this.messageData.number && !!this.messageData.templateName && this.isValidPhoneNumber;

    if (!this.hasParameters) return hasRequiredFields;

    // Check if all parameters are filled
    const allParametersFilled = this.componentsWithParameters.every((component) =>
      this.getComponentParameters(component).every(
        (_, index) => this.messageData.parameters[`${component.type}-${index}`],
      ),
    );

    return hasRequiredFields && allParametersFilled;
  }

  formatPhoneNumber() {
    // Remove any non-digit characters except the + at the start
    let number = this.messageData.number;
    if (number.startsWith('+')) {
      number = '+' + number.substring(1).replace(/\D/g, '');
    } else {
      number = number.replace(/\D/g, '');
    }

    // Only update if the number has actually changed
    if (number !== this.messageData.number) {
      this.messageData.number = number;
    }

    // Only validate if we have enough digits to be a valid number
    if (number.length >= 3) {
      this.isValidPhoneNumber = this.validatePhoneNumber(number);
    } else {
      this.isValidPhoneNumber = true;
    }
  }

  validatePhoneNumber(number: string): boolean {
    // WhatsApp requires a country code and the number should be in E.164 format
    // This regex checks for a + followed by 1-15 digits
    const phoneRegex = /^\+[1-9]\d{1,14}$/;
    return phoneRegex.test(number);
  }

  onBlur() {
    this.showError = true;
    this.isValidPhoneNumber = this.validatePhoneNumber(this.messageData.number);
  }

  async onSubmit() {
    if (!this.canSubmit) {
      return;
    }
    this.isSubmitting = true;
    try {
      const result = await WhatsAppApi.sendTemplateMessage(
        this.messageData.number,
        this.messageData.templateName,
        this.bot.id,
        this.messageData.parameters,
      );

      this.$emit('send', result);
      this.$notify.success('Successfully sent message');
      this.reset();
    } catch (e: any) {
      console.error('WhatsApp API Error:', e);

      let errorMessage = 'Failed to send message';

      if (e.response?.data?.error?.message) {
        // WhatsApp API error
        errorMessage = e.response.data.error.message;
      } else if (e.message) {
        // General error message
        errorMessage = e.message;
      }

      this.$notify.error({
        title: 'Sending message failed',
        description: errorMessage,
      });
    } finally {
      this.isSubmitting = false;
    }
  }

  @Watch('bot', { deep: true })
  reset() {
    this.messageData = this.resetValue();
    this.isValidPhoneNumber = true;
    this.showError = false;
  }

  resetValue(): SendMessageData {
    return {
      templateName: '',
      number: '',
      parameters: {},
    };
  }

  getFormattedText(component: WhatsAppTemplateMessageComponent): string {
    if (!component.text) return '';

    let formattedText = component.text;
    const paramMatches = component.text.match(/{{(\d+)}}/g) || [];

    paramMatches.forEach((match, index) => {
      const paramValue = this.messageData.parameters[`${component.type}-${index}`] || '';
      const paramPlaceholder = `<span class="font-medium">${paramValue || match}</span>`;
      formattedText = formattedText.replace(match, paramPlaceholder);
    });

    return formattedText;
  }
}
